import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePageTitle } from '@/hooks/use-page-title'
import { homeTarget } from '@/lib/access'

/*
 * Shell-content surfaces for access (VIS-329). They render instead of a page,
 * so nothing from a refused area mounts or is requested. A refused page and
 * an unknown route look the same, so a refusal never reveals that an area
 * exists. The `h1` takes focus on mount and the document title matches it.
 * `AccessMessage` is the shared message page, also used where a link leads to
 * something that isn't there (Guests: No guest found).
 */
export function AccessMessage({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  const heading = React.useRef<HTMLHeadingElement>(null)

  React.useEffect(() => {
    heading.current?.focus()
  }, [])

  usePageTitle(title)

  return (
    <div className="flex flex-col gap-4">
      <header className="pb-1">
        <h1 ref={heading} tabIndex={-1} className="text-2xl font-semibold text-fg-strong outline-none">
          {title}
        </h1>
      </header>
      <Card className="max-w-2xl">
        <CardContent className="flex flex-col items-start gap-4">
          <p className="text-pretty text-sm text-fg">{body}</p>
          {action}
        </CardContent>
      </Card>
    </div>
  )
}

/** A direct attempt at a page the caller may not open, or a route that doesn't exist. */
export function RefusedSurface() {
  const t = useT()
  const navigate = useNavigate()
  const { access, tenantName } = useAccess()
  const home = homeTarget(access)

  return (
    <AccessMessage
      title={t('core.access.refusedTitle')}
      body={t('core.access.refusedBody', { tenant: tenantName })}
      action={
        home ? (
          <Button variant="primary" size="lg" onClick={() => navigate(home.path)}>
            {t('core.access.goTo', { destination: t(home.labelKey) })}
          </Button>
        ) : null
      }
    />
  )
}

/** The caller holds no destination in the tenant; the rail is empty and the palette offers nothing. */
export function NoAreasSurface() {
  const t = useT()
  const { tenantName } = useAccess()
  return (
    <AccessMessage
      title={t('core.access.noAreasTitle', { tenant: tenantName })}
      body={t('core.access.noAreasBody', { tenant: tenantName })}
    />
  )
}
